'use client';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import getQualifiedImageUrl from '@utils/imageUrlProcessor';
import useCheckBreakpointActive from '@/hooks/useBreakpoint';
import React from 'react';
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md';
import { useSpringCarousel } from 'react-spring-carousel';
import { formatNumberForLocale } from '@utils/stringHelpers';
import Image from 'next/image';
import { isEmpty, size } from 'lodash-es';
import { useBreakpointHooks } from '@/utils/break-points.provider';

function BadgesCarousel(props) {
  const { userProfile, breakpoint = 'sm' } = props;
  const userBadges = userProfile.badges || [];
  const currentLevel = userProfile.level;

  const badgesAddOnData = [
    {
      id: -1,
      name: currentLevel && currentLevel.name,
      description: currentLevel && currentLevel.name,
      imageUrl: currentLevel && currentLevel.imageUrl,
    },
    {
      id: -2,
      name:
        userProfile && formatNumberForLocale(userProfile.points) + ' Nekies',
      description: 'Nekies',
      imageUrl: '/assets/icons/point.svg',
    },
  ];

  const isSmallScreen = useCheckBreakpointActive('sm');
  const isMediumScreen = useCheckBreakpointActive('md', 'down');
  const isLargeScreen = useCheckBreakpointActive('lg', 'down');
  const itemsPerSlide = isMediumScreen ? 1 : isLargeScreen ? 2 : 3;

  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  // on small screen, show level and points along with the badges
  const badges = userBadges.map(({ badge }) => badge);
  let carouselItems;
  if (badges.length) {
    // carouselItems =
    //   isSmallScreen || isMediumScreen ? badges.concat(badgesAddOnData) : badges;
    carouselItems = badges;
  } else {
    carouselItems = [];
  }
  const canNavigate = carouselItems.length > itemsPerSlide;

  if (carouselItems && carouselItems.length < itemsPerSlide) {
    const emptyItems = Array(itemsPerSlide - carouselItems.length)
      .fill()
      .map((i, index) => ({ id: `empty-${index}`, empty: true }));
    carouselItems.push(...emptyItems);
  }

  const renderItem = (badge) => {
    return (
      <div className="flex w-full flex-col items-center gap-2">
        {isEmpty(badge.imageUrl) ? (
          // Display a placeholder or some alternative content when imageUrl is empty
          <div className="h-12 w-12 bg-white"></div>
        ) : (
          <Image
            height={48}
            width={48}
            className="object-contain"
            src={getQualifiedImageUrl(badge.imageUrl) || ''}
            alt={badge.name}
          />
        )}
        <Typography variant="button" className="whitespace-nowrap text-center">
          {badge.name}
        </Typography>
      </div>
    );
  };

  const {
    carouselFragment,
    slideToPrevItem,
    slideToNextItem,
    getCurrentActiveItem,
  } = useSpringCarousel({
    slideAmount: 200,
    withLoop: canNavigate,
    itemsPerSlide: itemsPerSlide,
    gutter: isSmallScreen || isMediumScreen ? 0 : 40,
    items:
      carouselItems &&
      size(carouselItems) > 0 &&
      carouselItems.map((badge) => ({
        id: badge.id,
        renderItem: renderItem(badge),
      })),
  });

  const handleNextClick = () => {
    slideToNextItem();
  };

  const handlePrevClick = () => {
    slideToPrevItem();
  };

  return (
    <div className="mr-2 flex flex-col justify-around gap-4 md:flex-row lg:mr-0 lg:flex-row lg:gap-11">
      <div className="flex flex-row">
        <div
          className={`flex w-full flex-col items-center gap-2 border-r-[3px] ${
            isLessThanMediumScreen ? '' : 'pl-14 pr-14'
          }`}>
          <img
            src={getQualifiedImageUrl(currentLevel && currentLevel.imageUrl)}
            alt="reward level"
            className="w-15 h-11 object-contain"
          />
          {/* <Image
            height={45}
            width={75}
            className="object-contain"
            src={getQualifiedImageUrl(currentLevel && currentLevel.imageUrl) || ''}
            alt={currentLevel.name}
          /> */}
          <Typography variant="button" className="text-center">
            {currentLevel && currentLevel.name}
          </Typography>
        </div>
        <div
          className={`flex w-full flex-col items-center gap-2 md:border-r-[3px] lg:border-r-[3px] ${
            isLessThanMediumScreen ? '' : 'pl-14 pr-14'
          }`}>
          <img
            src="/assets/icons/point.svg"
            alt="point"
            className="w-15 h-11 object-contain"
          />
          {/* <Image
            height={50}
            width={60}
            className="object-contain"
            src={'/assets/icons/point.svg'}
            alt="point"
          /> */}
          <Typography
            variant="button"
            className="whitespace-nowrap text-center">
            {userProfile && formatNumberForLocale(userProfile.points)} Nekies
          </Typography>
        </div>
        <div></div>
      </div>
      <div className="flex items-center justify-center overflow-hidden">
        <Button
          variant="icon"
          onClick={handlePrevClick}
          disabled={!canNavigate}>
          <MdOutlineChevronLeft
            className={`h-8 w-8 ${
              canNavigate ? 'text-text-disabled' : 'text-body-background'
            }`}
          />
        </Button>
        <div className="flex-1 overflow-hidden">{carouselFragment}</div>
        <Button
          variant="icon"
          onClick={handleNextClick}
          disabled={!canNavigate}>
          <MdOutlineChevronRight
            className={`h-8 w-8 ${
              canNavigate ? 'text-text-disabled' : 'text-body-background'
            }`}
          />
        </Button>
      </div>
    </div>
  );
}

export default BadgesCarousel;
